import React, { useEffect, useState } from 'react';
import { HealthBuddy } from '../HealthBuddy';
import { CheckIcon, LockIcon, PlayIcon, FlameIcon, ArrowLeftIcon } from '../icons';
import { JOURNEY, CURRENT_STEP, MapStep, LessonPart } from '../../data/journey';
import { VoiceBar, VoiceActivity, useReadThenListen } from '../VoiceBar';
/* the voice pattern (VoiceBar + useReadThenListen + VoiceActivity) lives in
   ../VoiceBar — shared with Care Plans and any modal that adopts voice mode */
export type { VoiceActivity } from '../VoiceBar';
/**
 * Journey bottom modal — the Duolingo-style 12-week programme (card-flows §8).
 *
 *   1. Map     — the journey path: completed ✓ steps, the current step with
 *               Resume + progress arc, locked future steps, streak, Sunny's note
 *   2. Lesson  — full-modal screens: curated content → questions with warm,
 *               no-punishment feedback
 *   3. Done    — step-complete celebration · "Done for today"
 *               (the journey encourages stopping, not bingeing)
 *
 * Conversational entry skips the map: "resume my journey" opens the current
 * step exactly where it was left (startAt="lesson").
 *
 * VOICE MODE (toggled by the Sunny avatar in the modal header):
 * Sunny reads the content, questions and answer options aloud; the user
 * answers by speaking. A wrong spoken answer gets a warm "try again" (never
 * a reveal, never a penalty); a right one celebrates and listens for
 * "continue". Everything spoken also exists as touch — voice is a layer,
 * not the product. In the prototype, tapping a quoted phrase simulates
 * saying it.
 */
export type JourneyCondition = {
  id: string;
  label: string;
  programme: string;
  icon: (p: {
    size?: number;
    className?: string;
    strokeWidth?: number;
  }) => React.ReactElement;
  tint: string;
};
export function JourneyModal({
  onDone,
  startAt = 'map',
  voiceMode = false,
  onVoiceActivity,
  conditions = []
}: {
  onDone?: () => void;
  startAt?: 'map' | 'lesson';
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
  conditions?: JourneyCondition[];
}) {
  const multiple = conditions.length > 1;
  const [active, setActive] = useState<JourneyCondition | null>(conditions[0] ?? null);
  const [view, setView] = useState<'picker' | 'map' | 'lesson' | 'complete'>(multiple && startAt === 'map' ? 'picker' : startAt);
  const programme = active?.programme;
  if (view === 'picker') return <JourneyPicker conditions={conditions} onPick={c => {
    setActive(c);
    setView('map');
  }} data-id="element-434" />;
  if (view === 'complete') return <StepComplete onDone={onDone} voiceMode={voiceMode} onVoiceActivity={onVoiceActivity} data-id="element-435" />;
  if (view === 'lesson') return <LessonPlayer onExit={() => setView('map')} onComplete={() => setView('complete')} voiceMode={voiceMode} onVoiceActivity={onVoiceActivity} data-id="element-436" />;
  return <JourneyMap onResume={() => setView('lesson')} voiceMode={voiceMode} onVoiceActivity={onVoiceActivity} programme={programme} onBack={multiple ? () => setView('picker') : undefined} data-id="element-437" />;
}
/* ============================================================
   0 — the journey picker (shown when the user tracks >1 condition)
   ============================================================ */
function JourneyPicker({
  conditions,
  onPick
}: {
  conditions: JourneyCondition[];
  onPick: (c: JourneyCondition) => void;
}) {
  return <div className="px-5 pb-10 pt-2" data-id="element-438">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-coral-deep" data-id="element-439">
        Your journeys
      </p>
      <h2 className="font-display mt-1 pr-20 text-[22px] font-semibold leading-tight text-ink" data-id="element-440">
        Which journey today?
      </h2>
      <p className="mt-2 text-[13px] leading-snug text-ink-soft" data-id="element-441">
        You're tracking {conditions.length} conditions. Pick the journey you'd
        like to continue.
      </p>

      <div className="mt-5 space-y-2.5" data-id="element-442">
        {conditions.map(c => {
        const Icon = c.icon;
        return <button key={c.id} onClick={() => onPick(c)} className="flex w-full items-center gap-3.5 rounded-card border border-line bg-cream px-4 py-4 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]" data-id="element-443">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-${c.tint}-soft text-${c.tint}-deep`} data-id="element-444">
                <Icon size={22} data-id="element-445" />
              </span>
              <div className="min-w-0 flex-1" data-id="element-446">
                <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-447">
                  {c.label}
                </h3>
                <p className="mt-0.5 truncate text-[13px] leading-snug text-ink-soft" data-id="element-448">
                  {c.programme}
                </p>
              </div>
            </button>;
      })}
      </div>
    </div>;
}
/* ============================================================
   1 — the journey map
   ============================================================ */
export function JourneyMap({
  onResume,
  voiceMode = false,
  onVoiceActivity,
  programme,
  onBack
}: {
  onResume: () => void;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
  programme?: string;
  onBack?: () => void;
}) {
  const phase = useReadThenListen(voiceMode, onVoiceActivity, 2200);
  return <div className="px-5 pb-10 pt-2" data-id="element-449">
      {/* header */}
      {onBack && <button onClick={onBack} className="mb-2 -ml-1 flex items-center gap-1 text-[12.5px] font-bold text-coral-deep transition-colors hover:text-coral-deep/70" data-id="element-450">
          <ArrowLeftIcon size={15} strokeWidth={2.4} data-id="element-451" />
          All journeys
        </button>}
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-coral-deep" data-id="element-452">
        Journey · Week {JOURNEY.currentWeek} of {JOURNEY.totalWeeks}
      </p>
      <h2 className="font-display mt-1 pr-20 text-[22px] font-semibold leading-tight text-ink" data-id="element-453">
        {programme ?? JOURNEY.programme}
      </h2>

      {/* streak */}
      <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-butter-soft px-3.5 py-2" data-id="element-454">
        <FlameIcon size={15} strokeWidth={2} className="text-butter-deep" data-id="element-455" />
        <span className="text-[12.5px] font-bold text-butter-deep" data-id="element-456">
          {JOURNEY.streakDays}-day streak
        </span>
      </div>

      {/* voice mode — Sunny reads where you're up to, then listens */}
      {voiceMode && <VoiceBar mode={phase} hint={phase === 'reading' ? 'Week 7, Pollen season — you’re 2 parts into Pollen season tactics…' : 'Listening — just say what you’d like to do'} sayables={phase === 'listening' ? [{
      label: 'Resume',
      onSay: onResume
    }] : undefined} data-id="element-457" />}

      {/* the path */}
      <div className="relative mt-6" data-id="element-458">
        {/* the connecting line behind the step circles */}
        <div className="absolute bottom-4 left-[19px] top-4 w-[2px] rounded-full bg-linen-deep" data-id="element-459" />

        {/* weeks 1–5, collapsed */}
        <div className="relative flex items-center gap-3.5 py-2" data-id="element-460">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sage text-cream shadow-card" data-id="element-461">
            <CheckIcon size={16} strokeWidth={2.5} data-id="element-462" />
          </span>
          <div data-id="element-463">
            <h3 className="font-display text-[14.5px] font-semibold text-ink" data-id="element-464">
              {JOURNEY.earlierWeeks.label}
            </h3>
            <p className="text-[12px] font-medium text-sage-deep" data-id="element-465">
              {JOURNEY.earlierWeeks.detail}
            </p>
          </div>
        </div>

        {/* weeks 6–8 — the real steps */}
        {JOURNEY.weeks.map(week => <div key={week.week} className="relative" data-id="element-466">
            {/* week label */}
            <p className="ml-[52px] mt-4 text-[10.5px] font-bold uppercase tracking-[0.16em] text-ink-faint" data-id="element-467">
              Week {week.week} · {week.title}
            </p>
            {week.steps.map(step => step.status === 'current' ? <CurrentStepCard key={step.id} step={step} onResume={onResume} data-id="element-468" /> : <StepRow key={step.id} step={step} data-id="element-469" />)}
          </div>)}

        {/* weeks 9–12 — locked, titles visible so the journey feels finite */}
        {JOURNEY.laterWeeks.map(week => <div key={week.week} className="relative flex items-center gap-3.5 py-2 opacity-60" data-id="element-470">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-linen-deep bg-linen text-ink-faint" data-id="element-471">
              <LockIcon size={15} data-id="element-472" />
            </span>
            <div data-id="element-473">
              <h3 className="font-display text-[14px] font-semibold text-ink-soft" data-id="element-474">
                {week.title}
              </h3>
              <p className="text-[11.5px] font-medium text-ink-faint" data-id="element-475">
                Week {week.week}
              </p>
            </div>
          </div>)}
      </div>

      {/* Sunny's note — authored encouragement that references real activity */}
      <div className="mt-6 flex items-start gap-3 rounded-card bg-teal-soft p-4" data-id="element-476">
        <HealthBuddy size={38} mood="idle" className="shrink-0" data-id="element-477" />
        <div data-id="element-478">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-teal-deep" data-id="element-479">
            Sunny's note
          </p>
          <p className="mt-1 text-[13px] leading-snug text-ink" data-id="element-480">
            {JOURNEY.sunnysNote}
          </p>
        </div>
      </div>
    </div>;
}
/* a completed or locked step on the path */
function StepRow({
  step
}: {
  step: MapStep;
}) {
  const done = step.status === 'done';
  return <div className={`relative flex items-center gap-3.5 py-2 ${done ? '' : 'opacity-60'}`} data-id="element-481">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${done ? 'bg-sage text-cream shadow-card' : 'border-2 border-linen-deep bg-linen text-ink-faint'}`} data-id="element-482">
        {done ? <CheckIcon size={16} strokeWidth={2.5} data-id="element-483" /> : <LockIcon size={15} data-id="element-484" />}
      </span>
      <div data-id="element-485">
        <h3 className={`font-display text-[14.5px] font-semibold ${done ? 'text-ink' : 'text-ink-soft'}`} data-id="element-486">
          {step.title}
        </h3>
        <p className="text-[11.5px] font-medium text-ink-faint" data-id="element-487">
          {step.minutes} min{done ? ' · tap to review' : ''}
        </p>
      </div>
    </div>;
}
/* the current step — large highlighted card with Resume + progress arc */
function CurrentStepCard({
  step,
  onResume
}: {
  step: MapStep;
  onResume: () => void;
}) {
  const done = CURRENT_STEP.partsDone;
  const total = CURRENT_STEP.parts.length;
  return <div className="dhp-fade-up relative my-2 rounded-card border-2 border-coral bg-cream p-4 shadow-card-hover" data-id="element-488">
      <div className="flex items-start gap-3.5" data-id="element-489">
        <ProgressArc done={done} total={total} data-id="element-490" />
        <div className="min-w-0 flex-1" data-id="element-491">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-coral-deep" data-id="element-492">
            Current step
          </p>
          <h3 className="font-display mt-0.5 text-[16.5px] font-semibold leading-tight text-ink" data-id="element-493">
            {step.title}
          </h3>
          <p className="mt-0.5 text-[12px] font-medium text-ink-soft" data-id="element-494">
            {step.minutes} min · {done} of {total} parts done
          </p>
        </div>
      </div>
      <button onClick={onResume} className="mt-3.5 flex w-full items-center justify-center gap-2 rounded-full bg-coral py-3.5 text-[14.5px] font-bold text-cream shadow-card transition-transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-495">
        <PlayIcon size={14} data-id="element-496" />
        Resume
      </button>
    </div>;
}
/* progress arc — parts done out of total */
function ProgressArc({
  done,
  total
}: {
  done: number;
  total: number;
}) {
  const r = 17;
  const c = 2 * Math.PI * r;
  return <svg width="46" height="46" viewBox="0 0 46 46" className="shrink-0" data-id="element-497">
      <circle cx="23" cy="23" r={r} fill="none" stroke="var(--dhp-linen-deep)" strokeWidth="4.5" data-id="element-498" />
      <circle cx="23" cy="23" r={r} fill="none" stroke="var(--dhp-coral)" strokeWidth="4.5" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - done / total)} transform="rotate(-90 23 23)" data-id="element-499" />
      <text x="23" y="27" textAnchor="middle" fill="var(--dhp-ink)" fontSize="11.5" fontWeight="700" fontFamily="Karla, sans-serif" data-id="element-500">
        {done}/{total}
      </text>
    </svg>;
}
/* ============================================================
   2 — the lesson player
   ============================================================ */
export function LessonPlayer({
  onExit,
  onComplete,
  voiceMode = false,
  onVoiceActivity
}: {
  onExit: () => void;
  onComplete: () => void;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
}) {
  const parts = CURRENT_STEP.parts;
  /* resume exactly where the user left off (2 of 5 parts done) */
  const [partIndex, setPartIndex] = useState(CURRENT_STEP.partsDone);
  const part = parts[partIndex];
  const advance = () => {
    if (partIndex + 1 >= parts.length) onComplete();else setPartIndex(i => i + 1);
  };
  return <div className="flex flex-col px-5 pb-10 pt-2" style={{
    minHeight: 600
  }} data-id="element-501">
      {/* top bar — back to map + part progress */}
      <div className="flex shrink-0 items-center gap-3 pr-20" data-id="element-502">
        <button onClick={onExit} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-linen text-ink-soft transition-transform active:scale-90" data-id="element-503">
          <ArrowLeftIcon size={15} data-id="element-504" />
        </button>
        <div className="flex flex-1 gap-1.5" data-id="element-505">
          {parts.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < partIndex ? 'bg-coral' : i === partIndex ? 'bg-coral opacity-40' : 'bg-linen-deep'}`} data-id="element-506" />)}
        </div>
      </div>

      {/* key resets internal state when the part changes */}
      {part.kind === 'content' ? <ContentScreen key={partIndex} part={part} onContinue={advance} voiceMode={voiceMode} onVoiceActivity={onVoiceActivity} data-id="element-507" /> : <QuestionScreen key={partIndex} part={part} onContinue={advance} voiceMode={voiceMode} onVoiceActivity={onVoiceActivity} data-id="element-508" />}
    </div>;
}
/* a curated content chunk — same primitives as the Health Library */
function ContentScreen({
  part,
  onContinue,
  voiceMode = false,
  onVoiceActivity
}: {
  part: Extract<LessonPart, {
    kind: 'content';
  }>;
  onContinue: () => void;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
}) {
  const [playing, setPlaying] = useState(false);
  const phase = useReadThenListen(voiceMode, onVoiceActivity, 3000);
  /* in voice mode the narration plays automatically; the manual button is for touch mode */
  const reading = voiceMode ? phase === 'reading' : playing;
  return <div className="dhp-fade-up flex flex-1 flex-col" data-id="element-509">
      <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.16em] text-coral-deep" data-id="element-510">
        Week {CURRENT_STEP.week} · {CURRENT_STEP.title}
      </p>
      <h2 className="font-display mt-1.5 text-[24px] font-semibold leading-tight text-ink" data-id="element-511">
        {part.heading}
      </h2>

      {/* narration — the buddy reads the content aloud */}
      {part.narrated && !voiceMode && <button onClick={() => setPlaying(p => !p)} className={`mt-3.5 inline-flex w-fit items-center gap-2 rounded-full px-3.5 py-2 transition-colors ${playing ? 'bg-teal text-cream' : 'bg-teal-soft text-teal-deep'}`} data-id="element-512">
          <PlayIcon size={12} data-id="element-513" />
          <span className="text-[12px] font-bold" data-id="element-514">
            {playing ? 'Sunny is reading…' : 'Listen with Sunny'}
          </span>
        </button>}

      <div className="mt-4 flex flex-col gap-3.5" data-id="element-515">
        {part.paragraphs.map((para, i) => <p key={i} className="text-[14.5px] leading-relaxed text-ink-soft" data-id="element-516">
            {para}
          </p>)}
      </div>

      {/* the buddy keeps the user company while reading */}
      {reading && <div className="mt-5 flex justify-center" data-id="element-517">
          <HealthBuddy size={56} mood="speaking" data-id="element-518" />
        </div>}

      <div className="min-h-[24px] flex-1" data-id="element-519" />

      {/* voice mode — Sunny reads automatically, then listens for "continue" */}
      {voiceMode && <VoiceBar mode={phase} hint={phase === 'reading' ? 'Sunny is reading this aloud…' : 'Listening — say “continue” when you’re ready'} sayables={phase === 'listening' ? [{
      label: 'Continue',
      onSay: onContinue
    }] : undefined} data-id="element-520" />}

      <button onClick={onContinue} className="mt-4 w-full rounded-full bg-coral py-4 text-[15px] font-bold text-cream shadow-card transition-transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-521">
        Continue
      </button>
    </div>;
}
/* a question screen — immediate, warm feedback; no hearts, no lives, no red X.
   Voice mode: Sunny reads the question + options aloud; a wrong spoken answer
   gets a warm "try again" (no reveal); a right one celebrates, then Sunny
   listens for "continue". */
function QuestionScreen({
  part,
  onContinue,
  voiceMode = false,
  onVoiceActivity
}: {
  part: Extract<LessonPart, {
    kind: 'question';
  }>;
  onContinue: () => void;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  const [tried, setTried] = useState<number[]>([]);
  const [voicePhase, setVoicePhase] = useState<'reading' | 'awaiting-answer' | 'speaking-feedback' | 'awaiting-continue'>('reading');
  const answered = picked !== null;
  const correct = picked === part.correctIndex;
  const isTrueFalse = part.type === 'truefalse';
  /* voice: read the question + options aloud, then listen for an answer */
  useEffect(() => {
    if (!voiceMode) {
      onVoiceActivity?.('idle');
      return;
    }
    setVoicePhase('reading');
    onVoiceActivity?.('speaking');
    const t = setTimeout(() => {
      setVoicePhase('awaiting-answer');
      onVoiceActivity?.('listening');
    }, 2800);
    return () => clearTimeout(t);
  }, [voiceMode]);
  /* a spoken answer (simulated by tapping a quoted phrase) */
  const speakAnswer = (i: number) => {
    if (answered) return;
    if (i === part.correctIndex) {
      setPicked(i);
      setVoicePhase('speaking-feedback');
      onVoiceActivity?.('speaking');
      setTimeout(() => {
        setVoicePhase('awaiting-continue');
        onVoiceActivity?.('listening');
      }, 2400);
    } else {
      setTried(t => t.includes(i) ? t : [...t, i]);
      setVoicePhase('speaking-feedback');
      onVoiceActivity?.('speaking');
      setTimeout(() => {
        setVoicePhase('awaiting-answer');
        onVoiceActivity?.('listening');
      }, 2400);
    }
  };
  /* touch tap on an option — voice mode routes through the spoken (try-again)
     flow; touch mode keeps the classic reveal */
  const tapOption = (i: number) => {
    if (answered) return;
    if (voiceMode) speakAnswer(i);else setPicked(i);
  };
  /* voice try-again state — a wrong spoken attempt exists and nothing correct yet */
  const showTryAgain = voiceMode && !answered && tried.length > 0;
  return <div className="dhp-fade-up flex flex-1 flex-col" data-id="element-522">
      <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.16em] text-coral-deep" data-id="element-523">
        {isTrueFalse ? 'True or false?' : 'Quick question'}
      </p>
      <h2 className="font-display mt-1.5 text-[21px] font-semibold leading-snug text-ink" data-id="element-524">
        {part.prompt}
      </h2>

      <div className={`mt-5 grid gap-2.5 ${isTrueFalse ? 'grid-cols-2' : 'grid-cols-1'}`} data-id="element-525">
        {part.options.map((option, i) => {
        const isAnswer = i === part.correctIndex;
        const isPicked = i === picked;
        const wasTried = tried.includes(i);
        /* feedback styling — the right answer turns sage when answered; a wrong
        pick gets a warm butter tone (never red); voice try-again marks the
        attempted option but never reveals the right one */
        let cls = 'border-linen-deep bg-cream hover:-translate-y-0.5 hover:border-coral hover:shadow-card-hover';
        if (answered) {
          if (isAnswer) cls = 'border-sage bg-sage-soft';else if (isPicked) cls = 'border-butter bg-butter-soft';else cls = 'border-linen-deep bg-cream opacity-40';
        } else if (wasTried) {
          cls = 'border-butter bg-butter-soft opacity-60';
        }
        return <button key={i} disabled={answered || wasTried} onClick={() => tapOption(i)} className={`flex items-center justify-between rounded-2xl border-2 p-4 text-left transition-all duration-300 active:scale-[0.98] ${cls} ${isTrueFalse ? 'justify-center py-6' : ''}`} data-id="element-526">
              <span className="text-[14.5px] font-semibold text-ink" data-id="element-527">
                {option}
              </span>
              {answered && isAnswer && !isTrueFalse && <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sage text-cream" data-id="element-528">
                  <CheckIcon size={12} strokeWidth={2.5} data-id="element-529" />
                </span>}
            </button>;
      })}
      </div>

      <div className="min-h-[16px] flex-1" data-id="element-530" />

      {/* feedback — warm either way; questions reinforce, never test */}
      {(answered || showTryAgain) && <div className="dhp-bounce-in mt-5 flex items-start gap-3 rounded-card bg-linen p-4" data-id="element-531">
          <HealthBuddy size={42} mood={answered && correct ? 'celebrating' : 'idle'} className="shrink-0" data-id="element-532" />
          <div data-id="element-533">
            <p className={`text-[12px] font-bold ${answered && correct ? 'text-sage-deep' : 'text-teal-deep'}`} data-id="element-534">
              {answered ? correct ? 'That’s it!' : 'Good thinking — here’s the key bit' : 'Not quite — but you’re close'}
            </p>
            <p className="mt-0.5 text-[13px] leading-snug text-ink" data-id="element-535">
              {answered ? correct ? part.correctFeedback : part.incorrectFeedback : 'Have another think — say your answer whenever you’re ready. There’s no rush and no penalty.'}
            </p>
          </div>
        </div>}

      {/* voice mode bar */}
      {voiceMode && <VoiceBar mode={voicePhase === 'reading' || voicePhase === 'speaking-feedback' ? 'reading' : 'listening'} hint={voicePhase === 'reading' ? 'Sunny is reading the question and answers…' : voicePhase === 'speaking-feedback' ? answered ? 'Sunny is celebrating with you…' : 'Sunny is encouraging you to try again…' : voicePhase === 'awaiting-continue' ? 'Listening — say “continue” to keep going' : 'Listening — just say your answer'} sayables={voicePhase === 'awaiting-answer' ? part.options.map((opt, i) => ({
      label: opt,
      onSay: () => speakAnswer(i),
      idx: i
    })).filter(s => !tried.includes(s.idx)).map(s => ({
      label: s.label,
      onSay: s.onSay
    })) : voicePhase === 'awaiting-continue' ? [{
      label: 'Continue',
      onSay: onContinue
    }] : undefined} data-id="element-536" />}

      {answered && <button onClick={onContinue} className="dhp-fade-up mt-4 w-full rounded-full bg-coral py-4 text-[15px] font-bold text-cream shadow-card transition-transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-537">
          Continue
        </button>}
    </div>;
}
/* ============================================================
   3 — step complete
   ============================================================ */
export function StepComplete({
  onDone,
  voiceMode = false,
  onVoiceActivity
}: {
  onDone?: () => void;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
}) {
  const phase = useReadThenListen(voiceMode, onVoiceActivity, 2600);
  return <div className="flex flex-col items-center px-5 pb-12 pt-8 text-center" data-id="element-538">
      <HealthBuddy size={104} mood="celebrating" data-id="element-539" />
      <h2 className="font-display mt-5 text-[26px] font-semibold text-ink" data-id="element-540">
        Step complete!
      </h2>
      <p className="mt-2 max-w-[280px] text-[14.5px] leading-relaxed text-ink-soft" data-id="element-541">
        That’s <span className="font-bold text-ink" data-id="element-542">{CURRENT_STEP.title}</span>{' '}
        done — you’re 3 steps into Week {CURRENT_STEP.week} now.
      </p>

      {/* +1 step · streak — progress, never punishment */}
      <div className="dhp-stagger mt-6 flex items-stretch gap-3" data-id="element-543">
        <div className="rounded-2xl bg-coral-soft px-5 py-3.5" data-id="element-544">
          <p className="font-display text-[22px] font-semibold leading-none text-coral-deep" data-id="element-545">
            +1
          </p>
          <p className="mt-1 text-[11px] font-bold text-coral-deep" data-id="element-546">step</p>
        </div>
        <div className="rounded-2xl bg-butter-soft px-5 py-3.5" data-id="element-547">
          <p className="font-display flex items-center justify-center gap-1 text-[22px] font-semibold leading-none text-butter-deep" data-id="element-548">
            <FlameIcon size={18} strokeWidth={2} data-id="element-549" />
            {JOURNEY.streakDays + 1}
          </p>
          <p className="mt-1 text-[11px] font-bold text-butter-deep" data-id="element-550">
            day streak
          </p>
        </div>
      </div>

      {/* preview of the next step */}
      <div className="mt-6 w-full rounded-card border border-linen-deep bg-linen p-4 text-left" data-id="element-551">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-faint" data-id="element-552">
          Next time
        </p>
        <div className="mt-2 flex items-center gap-3" data-id="element-553">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cream text-ink-faint shadow-card" data-id="element-554">
            <LockIcon size={15} data-id="element-555" />
          </span>
          <div data-id="element-556">
            <h3 className="font-display text-[14.5px] font-semibold text-ink" data-id="element-557">
              {CURRENT_STEP.nextStep.title}
            </h3>
            <p className="text-[12px] font-medium text-ink-soft" data-id="element-558">
              {CURRENT_STEP.nextStep.minutes} min · unlocks tomorrow
            </p>
          </div>
        </div>
      </div>

      {/* health pacing, not engagement maximisation */}
      <p className="mt-4 max-w-[260px] text-[12px] leading-snug text-ink-faint" data-id="element-559">
        No need to do more today — little and often is how this works.
      </p>

      {/* voice mode — Sunny celebrates out loud, then listens */}
      {voiceMode && <div className="w-full text-left" data-id="element-560">
          <VoiceBar mode={phase} hint={phase === 'reading' ? 'Sunny is celebrating with you…' : 'Listening — say “done for today” to finish'} sayables={phase === 'listening' ? [{
        label: 'Done for today',
        onSay: () => onDone?.()
      }] : undefined} data-id="element-561" />
        </div>}

      <button onClick={onDone} className="mt-5 w-full rounded-full bg-teal py-4 text-[15px] font-bold text-cream shadow-card transition-transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-562">
        Done for today
      </button>
    </div>;
}